import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, X } from 'lucide-react';
import { useTrades, useCancelTrade } from '@/api/trades/use-trades';
import { useTransactions } from '@/api/transactions/use-transactions';
import MobileFloatingNav from '@/components/mobile/MobileFloatingNav';
import { formatCurrencyAmount } from '@/utils/formatters';
import { Loader2 } from 'lucide-react';
import StatementDownloadButton from '@/components/statements/StatementDownloadButton';
import { useSnackbar } from 'notistack';

const MobileHistoryPage: React.FC = () => {
    const { t } = useTranslation('mobile');
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const [activeTab, setActiveTab] = useState<'transactions' | 'orders' | 'openPending'>(
        'transactions'
    );

    // Fetch closed trades (history)
    const {
        data: closedTradesData,
        isLoading: tradesLoading,
        refetch: refetchClosed,
    } = useTrades({
        status: 'closed',
        per_page: 50,
    });

    // Fetch pending and open for Open & Pending tab
    const {
        data: pendingTradesData,
        isLoading: pendingLoading,
        refetch: refetchPending,
    } = useTrades({
        status: 'pending',
        per_page: 50,
    });
    const {
        data: openTradesData,
        isLoading: openLoading,
        refetch: refetchOpen,
    } = useTrades({
        status: 'open',
        per_page: 50,
    });

    const cancelTradeMutation = useCancelTrade();

    // Fetch transactions
    const { data: transactionsData, isLoading: transactionsLoading } = useTransactions({
        per_page: 50,
    });

    const closedTrades = closedTradesData?.trades || [];
    const pendingTrades = pendingTradesData?.trades || [];
    const openTrades = openTradesData?.trades || [];
    // API returns { success: true, data: [...], meta: {...} }
    // So transactionsData.data is the array directly
    const transactions = transactionsData?.data || [];

    const handleCancelPending = async (trade: { id: string }) => {
        try {
            await cancelTradeMutation.mutateAsync({ id: trade.id });
            enqueueSnackbar(t('orderCancelledSuccess'), { variant: 'success' });
            refetchPending();
        } catch {
            enqueueSnackbar(t('failedToCancelOrder'), { variant: 'error' });
        }
    };

    const isLoading =
        activeTab === 'transactions'
            ? tradesLoading || transactionsLoading
            : activeTab === 'orders'
              ? tradesLoading || transactionsLoading
              : pendingLoading || openLoading;

    return (
        <div className="min-h-screen text-foreground pb-32">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-4 border-b border-border">
                <button onClick={() => navigate('/')}>
                    <ArrowLeft className="w-6 h-6" />
                </button>
                <span className="font-semibold text-lg">{t('historyTitle')}</span>
                <StatementDownloadButton variant="mobile" />
            </div>

            {/* Tabs */}
            <div className="flex border-b border-border">
                <button
                    onClick={() => setActiveTab('transactions')}
                    className={`flex-1 py-3 text-sm font-medium min-w-0 ${
                        activeTab === 'transactions'
                            ? 'text-primary border-b-2 border-primary'
                            : 'text-muted-foreground'
                    }`}
                >
                    {t('transactions')}
                </button>
                <button
                    onClick={() => setActiveTab('openPending')}
                    className={`flex-1 py-3 text-sm font-medium min-w-0 ${
                        activeTab === 'openPending'
                            ? 'text-primary border-b-2 border-primary'
                            : 'text-muted-foreground'
                    }`}
                >
                    {t('openAndPendingTab')}
                </button>
                <button
                    onClick={() => setActiveTab('orders')}
                    className={`flex-1 py-3 text-sm font-medium min-w-0 ${
                        activeTab === 'orders'
                            ? 'text-primary border-b-2 border-primary'
                            : 'text-muted-foreground'
                    }`}
                >
                    {t('closedOrders')}
                </button>
            </div>

            {/* Content */}
            <div className="px-4 py-4">
                {isLoading ? (
                    <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin" />
                    </div>
                ) : activeTab === 'openPending' ? (
                    <div className="space-y-6">
                        {/* Pending orders section */}
                        <div>
                            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                                {t('pendingOrdersSection')}
                            </h3>
                            {pendingTrades.length === 0 ? (
                                <div className="text-center text-muted-foreground py-6 rounded-xl trader-glass">
                                    {t('noPendingOrders')}
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {pendingTrades.map((trade: any) => (
                                        <div
                                            key={trade.id}
                                            className="trader-glass rounded-2xl p-4"
                                        >
                                            <div className="flex items-center justify-between mb-2">
                                                <div className="flex items-center gap-3">
                                                    <div
                                                        className={`px-2 py-1 rounded text-xs font-semibold ${
                                                            trade.side === 'buy'
                                                                ? 'trader-pill-positive'
                                                                : 'trader-pill-negative'
                                                        }`}
                                                    >
                                                        {trade.side?.toUpperCase() || 'N/A'}
                                                    </div>
                                                    <div>
                                                        <div className="font-semibold text-sm">
                                                            {trade.instrument?.symbol || 'N/A'}
                                                        </div>
                                                        <div className="text-xs text-muted-foreground">
                                                            {trade.type?.toUpperCase() || 'MARKET'}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <div className="text-sm font-semibold">
                                                        {Number(trade.lot_size || 0).toFixed(2)}{' '}
                                                        {t('lots')}
                                                    </div>
                                                    {trade.entry_price && (
                                                        <div className="text-xs text-muted-foreground">
                                                            @ {Number(trade.entry_price).toFixed(5)}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            {trade.expires_at && (
                                                <div className="text-xs text-muted-foreground pt-2 border-t border-border mb-2">
                                                    {t('expires')}:{' '}
                                                    {new Date(trade.expires_at).toLocaleString()}
                                                </div>
                                            )}
                                            <button
                                                onClick={() => handleCancelPending(trade)}
                                                disabled={cancelTradeMutation.isPending}
                                                className="w-full mt-2 py-2 trader-pill-negative hover:opacity-80 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                            >
                                                <X className="w-4 h-4" />
                                                {cancelTradeMutation.isPending
                                                    ? t('cancelling')
                                                    : t('cancelOrder')}
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        {/* Open positions section */}
                        <div>
                            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                                {t('openPositionsSection')}
                            </h3>
                            {openTrades.length === 0 ? (
                                <div className="text-center text-muted-foreground py-6 rounded-xl trader-glass">
                                    {t('noOpenPositions')}
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {openTrades.map((trade: any) => {
                                        const pnl = Number(trade.unrealized_pnl || 0);
                                        const pnlPositive = pnl >= 0;
                                        return (
                                            <div
                                                key={trade.id}
                                                onClick={() => navigate('/trade')}
                                                className="trader-glass rounded-2xl p-4 active:opacity-90"
                                            >
                                                <div className="flex items-center justify-between mb-2">
                                                    <div className="flex items-center gap-3">
                                                        <div
                                                            className={`px-2 py-1 rounded text-xs font-semibold ${
                                                                trade.side === 'buy'
                                                                    ? 'trader-pill-positive'
                                                                    : 'trader-pill-negative'
                                                            }`}
                                                        >
                                                            {trade.side?.toUpperCase() || 'N/A'}
                                                        </div>
                                                        <div>
                                                            <div className="font-semibold text-sm">
                                                                {trade.instrument?.symbol || 'N/A'}
                                                            </div>
                                                            <div className="text-xs text-muted-foreground">
                                                                {Number(
                                                                    trade.lot_size || 0
                                                                ).toFixed(2)}{' '}
                                                                {t('lots')}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div
                                                        className={`text-right ${
                                                            pnlPositive
                                                                ? 'text-trader-positive'
                                                                : 'text-trader-negative'
                                                        }`}
                                                    >
                                                        <div className="text-sm font-bold">
                                                            {pnlPositive ? '+' : ''}
                                                            {formatCurrencyAmount(
                                                                pnl,
                                                                trade.trading_account
                                                                    ?.base_currency || 'USD'
                                                            )}
                                                        </div>
                                                        <div className="text-xs text-muted-foreground">
                                                            {t('pnl')}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground pt-2 border-t border-border">
                                                    <div>
                                                        <span>{t('entry')}: </span>
                                                        <span className="text-foreground">
                                                            {trade.entry_price
                                                                ? Number(trade.entry_price).toFixed(
                                                                      5
                                                                  )
                                                                : 'N/A'}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                ) : activeTab === 'transactions' ? (
                    transactions.length === 0 ? (
                        <div className="text-center text-muted-foreground py-8">
                            {t('noTransactionsFound')}
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {transactions.map((transaction: any) => {
                                const amount = Number(transaction.amount || 0);
                                const isPositive =
                                    transaction.type === 'deposit' ||
                                    transaction.type === 'credit' ||
                                    transaction.type === 'bonus';
                                return (
                                    <div
                                        key={transaction.id}
                                        className="trader-glass rounded-2xl p-4"
                                    >
                                        <div className="flex items-center justify-between mb-2">
                                            <div>
                                                <div className="font-semibold text-sm">
                                                    {transaction.type?.toUpperCase() ||
                                                        'TRANSACTION'}
                                                </div>
                                                <div className="text-xs text-muted-foreground">
                                                    {transaction.status?.toUpperCase() || 'PENDING'}
                                                </div>
                                            </div>
                                            <div
                                                className={`text-right ${
                                                    isPositive
                                                        ? 'text-trader-positive'
                                                        : 'text-trader-negative'
                                                }`}
                                            >
                                                <div className="text-sm font-bold">
                                                    {isPositive ? '+' : ''}
                                                    {formatCurrencyAmount(
                                                        amount,
                                                        transaction.currency || 'USD'
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        {transaction.description && (
                                            <div
                                                translate="no"
                                                className="text-xs text-muted-foreground pt-2 border-t border-border"
                                            >
                                                {transaction.description}
                                            </div>
                                        )}
                                        {transaction.created_at && (
                                            <div className="text-xs text-muted-foreground pt-1">
                                                {new Date(transaction.created_at).toLocaleString()}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )
                ) : closedTrades.length === 0 ? (
                    <div className="text-center text-muted-foreground py-8">
                        {t('noClosedOrdersFound')}
                    </div>
                ) : (
                    <div className="space-y-3">
                        {closedTrades.map((trade: any) => {
                            const pnl = Number(trade.realized_pnl || 0);
                            const pnlPositive = pnl >= 0;
                            return (
                                <div key={trade.id} className="trader-glass rounded-2xl p-4">
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center gap-3">
                                            <div
                                                className={`px-2 py-1 rounded text-xs font-semibold ${
                                                    trade.side === 'buy'
                                                        ? 'trader-pill-positive'
                                                        : 'trader-pill-negative'
                                                }`}
                                            >
                                                {trade.side?.toUpperCase() || 'N/A'}
                                            </div>
                                            <div>
                                                <div className="font-semibold text-sm">
                                                    {trade.instrument?.symbol || 'N/A'}
                                                </div>
                                                <div className="text-xs text-muted-foreground">
                                                    {Number(trade.lot_size || 0).toFixed(2)}{' '}
                                                    {t('lots')}
                                                </div>
                                            </div>
                                        </div>
                                        <div
                                            className={`text-right ${
                                                pnlPositive
                                                    ? 'text-trader-positive'
                                                    : 'text-trader-negative'
                                            }`}
                                        >
                                            <div className="text-sm font-bold">
                                                {pnlPositive ? '+' : ''}
                                                {formatCurrencyAmount(
                                                    pnl,
                                                    trade.trading_account?.base_currency || 'USD'
                                                )}
                                            </div>
                                            <div className="text-xs text-muted-foreground">
                                                {t('pnl')}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground pt-2 border-t border-border">
                                        <div>
                                            <span>{t('entry')}: </span>
                                            <span className="text-foreground">
                                                {trade.entry_price
                                                    ? Number(trade.entry_price).toFixed(5)
                                                    : 'N/A'}
                                            </span>
                                        </div>
                                        <div>
                                            <span>{t('exit')}: </span>
                                            <span className="text-foreground">
                                                {trade.exit_price
                                                    ? Number(trade.exit_price).toFixed(5)
                                                    : 'N/A'}
                                            </span>
                                        </div>
                                    </div>
                                    {trade.closed_at && (
                                        <div className="text-xs text-muted-foreground pt-2">
                                            {t('closed')}:{' '}
                                            {new Date(trade.closed_at).toLocaleString()}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <MobileFloatingNav />
        </div>
    );
};

export default MobileHistoryPage;
